import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera'
import { Camera } from '@babylonjs/core/Cameras/camera'
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector'
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder'
import { Tools } from '@babylonjs/core/Misc/tools'
import AnimationTimer from './AnimationTimer'
import { Frame } from './Frame'
import { Settings } from './Settings'

export const CameraDefaults = {
	fov: .2,

	panningSensibility: 10000, // Panning speed. Lower is faster
	panningInertia: 0.2,

	rotationSensibility: 2000, // Rotation speed. Lower is faster
	rotationInertia: .9,

	minZoom: 5,
	startZoom: 1,
	maxZoom: .5,

	wheelPrecision: 400 * 2,

	panBackInertia: .51
}

export enum CameraAngle {
	Front = 'Front',
	Perspective = 'Perspective',
	Side = 'Side',
	Top = 'Top',
	Engraving = 'Engraving',
	Diamond = 'Diamond',
	Hinge = 'Hinge',
	Detail = 'Detail',
	Top2 = 'Top2'
}

const transforms: Record<CameraAngle, { target?: Vector3; alpha: number; beta: number; radius: number; node?: string; mesh?: string; side?: 'LEFT' | 'RIGHT'; useCenter?: boolean; offset?: Vector3 }> = {
	Front: {
		target: Vector3.Zero(),
		alpha: Tools.ToRadians(90),
		beta: Tools.ToRadians(90),
		radius: .93
	},
	Side: {
		useCenter: true,
		alpha: Tools.ToRadians(0),
		beta: Tools.ToRadians(90),
		radius: 1
	},
	Perspective: {
		target: new Vector3(0.008, 0, -0.051),
		alpha: Tools.ToRadians(57),
		beta: Tools.ToRadians(108),
		radius: 1.25
	},
	Top: {
		target: new Vector3(0, 0, -0.025),
		alpha: Tools.ToRadians(90),
		beta: Tools.ToRadians(10),
		radius: 1.3
	},
	Top2: {
		target: new Vector3(0, 0, -0.025),
		alpha: Tools.ToRadians(-90),
		beta: .01, // Hmm
		radius: 1
	},
	Detail: {
		node: 'TEMPLE-OUT',
		side: 'LEFT',
		alpha: 1.02,
		beta: 2.15,
		radius: .3,
		offset: new Vector3(0, -0.005, 0)
	},
	Hinge: {
		node: 'TEMPLE-OUT',
		side: 'LEFT',
		alpha: Tools.ToRadians(45),
		beta: Tools.ToRadians(90),
		radius: .2
	},
	Diamond: {
		mesh: 'DIAMOND',
		side: 'LEFT',
		alpha: Tools.ToRadians(45),
		beta: Tools.ToRadians(90),
		radius: .2
	},
	Engraving: {
		mesh: 'ENGRAVING-LEFT',
		side: 'LEFT',
		alpha: Tools.ToRadians(160),
		beta: Tools.ToRadians(90),
		radius: .1
	}
}

export class ScreenShotCamera extends ArcRotateCamera {
	constructor() {
		super('SCREENSHOT', Tools.ToRadians(90), Tools.ToRadians(90), 1, Vector3.Zero())
		this.minZ = 0.01 // NEAR
		this.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED
		this.fov = CameraDefaults.fov
	}
	transform(angle: CameraAngle) {
		let { target, alpha, beta, radius, useCenter, offset } = transforms[angle]

		if (transforms[angle].node) {
			target = this.getScene().transformNodes.find(x => (x.name == transforms[angle].node || x.name.startsWith(transforms[angle].node + '-')) && (transforms[angle].side == 'LEFT' ? x.absolutePosition.x > 0 : x.absolutePosition.x < 0))?.absolutePosition.clone()
		}
		if (useCenter) {
			const { min, max } = this.getScene().getTransformNodeByName('FRAME')?.getHierarchyBoundingVectors() || { min: Vector3.Zero(), max: Vector3.Zero() }
			target = min.add(max).scale(0.5)
			target.y = 0
			// this.center = min.add(max).scale(0.5);
			// this.size = max.subtract(min);
		}

		if (transforms[angle].mesh) {
			const mesh = this.getScene().meshes.find(x => (x.name == transforms[angle].mesh || x.name.startsWith(transforms[angle].mesh + '-')) && (transforms[angle].side == 'LEFT' ? x.absolutePosition.x > 0 : x.absolutePosition.x < 0))
			if (mesh) {
				const bounds = mesh.getHierarchyBoundingVectors()
				target = bounds.max.add(bounds.min).divide(new Vector3(2, 2, 2))
			} else {
				const placeholder = this.getScene().transformNodes.find(x => x.name == transforms[angle].mesh + '-PLACEHOLDER')
				if (placeholder) target = placeholder.absolutePosition.clone()
			}
		}

		if (target) this.target = target.clone()
		if (offset && this.target) this.target.addInPlace(offset)
		this.alpha = alpha
		this.beta = beta
		this.radius = radius

		return true
	}
}

export class MainCamera extends ArcRotateCamera {
	current: CameraAngle = CameraAngle.Front
	isMoving = false
	isAnimating = false

	autoPanBack = true

	screenOffset: Matrix
	maxZoom: number
	fixedMove = false

	constructor(canvas: HTMLCanvasElement) {
		super('MAIN', 0, 0, 1, Vector3.Zero())
		this.minZ = 0.01 // NEAR
		this.fov = CameraDefaults.fov

		this.upperRadiusLimit = CameraDefaults.minZoom
		this.lowerRadiusLimit = this.maxZoom = CameraDefaults.maxZoom

		this.angularSensibilityX = this.angularSensibilityY = CameraDefaults.rotationSensibility
		this.inertia = CameraDefaults.rotationInertia

		this.panningSensibility = Number.MAX_VALUE
		this.panningInertia = CameraDefaults.panningInertia
		this.panningDistanceLimit = 0

		this.useAutoRotationBehavior = true
		this.autoRotationBehavior.idleRotationSpeed = .005
		this.useAutoRotationBehavior = false

		// this.inertia = .96

		// this.pinchDeltaPercentage =
		this.wheelPrecision = CameraDefaults.wheelPrecision
		this.useNaturalPinchZoom = true

		// this.position = FrontCamera.POSITION
		// this.target = FrontCamera.TARGET
		// this.current = CameraAngle.Front

		this.attachControl(canvas, true)

		// this.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED
		this.fovMode = Camera.FOVMODE_VERTICAL_FIXED
		this.update()

		this.transform(this.current)

		// this.panningAxis = "Pan" ? Vector3.One() : Vector3.Zero()

		// this.angularSensibilityX = "Orbit-Horizontal" ? CameraDefaults.rotationSensibility : Number.MAX_VALUE
		// this.angularSensibilityY = "Orbit-Vertical" ? CameraDefaults.rotationSensibility : Number.MAX_VALUE

		// this.lowerRadiusLimit = 0.5
		// this.upperAlphaLimit = Tools.ToRadians(90)
		// this.lowerAlphaLimit = Tools.ToRadians(90)
		// this.upperBetaLimit = Tools.ToRadians(90)
		// this.lowerBetaLimit = Tools.ToRadians(90)

		// CameraMovement + Auto Pan-To-Center
		let prevPos = Vector3.Zero()
		let timeout: number
		this.getScene().onBeforeRenderObservable.add(info => {
			const dist = this.position.subtract(prevPos).length()

			const isMoving = !this.isAnimating && dist > 0
			if (isMoving && this.autoPanBack && !this.fixedMove) {
				this.target.scaleInPlace(1 - dist * CameraDefaults.panBackInertia)
				if (this.radius < this.maxZoom) {
					// console.log(this.radius, this.maxZoom)
					this.radius += dist * CameraDefaults.panBackInertia
					this.lowerRadiusLimit = this.radius
				}
			}
			this.fixedMove = false

			// const alphaDist = Math.abs(this.position.x - prevPos.x) //TODO: FIX
			// const betaDist = Math.abs(this.position.y - prevPos.y)
			/*if(this.autoPanBack) {
                this.target.x *= 1 - alphaDist * .9
                this.target.z *=  1 - betaDist * .9
                console.log(this.target)
            }*/

			prevPos = this.position.clone()
			if (!isMoving && this.isMoving && !timeout) {
				timeout = window.setTimeout(() => this.isMoving = false, 200)
			} else if (isMoving && !this.isMoving) {
				clearTimeout(timeout)
				timeout = null
				this.isMoving = true
			}
		})
	}

	getFrameTransform(angle: CameraAngle): [Vector3, Quaternion] {
		let transform = transforms[CameraAngle.Front]
		// const node = new TransformNode("TEMP-NODE")
		const node = CreateCylinder('TEMP', { height: .05, diameter: .01 })
		const cam = new ArcRotateCamera('TEMP-CAM', transform.alpha, transform.beta, transform.radius, transform.target)

		cam.update()
		node.setParent(cam)
		transform = transforms[angle]
		cam.alpha = transform.alpha

		cam.beta = transform.radius
		cam.target = transform.target
		cam.update()
		// node.setParent(null)

		return [node.absolutePosition.clone(), node.absoluteRotationQuaternion.clone()]
	}

	transform(angle: CameraAngle, animated: boolean = false) {
		this.autoPanBack = animated
		this.current = angle

		let { alpha, beta, radius, useCenter, offset } = transforms[angle]
		let target = transforms[angle].target?.clone()

		if (transforms[angle].node) {
			target = this.getScene().transformNodes.find(x => (x.name == transforms[angle].node || x.name.startsWith(transforms[angle].node + '-')) && (transforms[angle].side == 'LEFT' ? x.absolutePosition.x > 0 : x.absolutePosition.x < 0))?.absolutePosition.clone()
		}

		if (transforms[angle].mesh) {
			const mesh = this.getScene().meshes.find(x => (x.name == transforms[angle].mesh || x.name.startsWith(transforms[angle].mesh + '-')) && (transforms[angle].side == 'LEFT' ? x.absolutePosition.x > 0 : x.absolutePosition.x < 0))
			console.log('MESH NOT FOUND')
			if (!mesh) return
			const bounds = mesh.getHierarchyBoundingVectors()
			target = bounds.max.add(bounds.min).divide(new Vector3(2, 2, 2))
		}

		if (useCenter) {
			const { min, max } = this.getScene().getTransformNodeByName('FRAME')?.getHierarchyBoundingVectors() || { min: Vector3.Zero(), max: Vector3.Zero() }
			target = min.add(max).scale(0.5)
			target.y = 0
		}

		if (offset && target) {
			target.addInPlace(offset)
		}

		if (!target) {
			console.warn('UNKNOWN', this.current)
			return
		}

		function mod(n: number, m: number): number {
			return ((n % m) + m) % m
		}

		this.alpha = mod(this.alpha, 2 * Math.PI)
		if ((this.alpha - alpha) > Math.PI) this.alpha -= 2 * Math.PI

		if (radius < this.lowerRadiusLimit) {
			this.lowerRadiusLimit = radius
		}

		if (animated) {
			this.isAnimating = true
			this.isMoving = false
			new AnimationTimer(this.getScene(), {
				target: this,
				properties: ['target', 'alpha', 'beta', 'radius'],
				from: [this.target.clone(), this.alpha, /*mod(this.alpha, 2 * Math.PI)*/ this.beta, this.radius],
				to: [target, alpha, /*mod(alpha, 2 * Math.PI)*/ beta, radius],
				duration: Settings.cameraDuration,
				complete: () => {
					this.isAnimating = false
					this.autoPanBack = true
				}
			})
		} else {
			this.lowerRadiusLimit = Math.min(this.lowerRadiusLimit, radius)
			this.upperRadiusLimit = Math.max(this.upperRadiusLimit, radius)

			this.target = target
			this.alpha = alpha
			this.beta = beta
			this.radius = radius
			this.fixedMove = true
			requestAnimationFrame(() => this.autoPanBack = true)
		}
	}
}

interface CustomCamera {
	align(frame: Frame)
}
